/**
 * Colours the track by gradient, porting the `incline` provider of
 * `plugin/RoutingPathQuality.js`.
 *
 * The legacy client used `leaflet-hotline` to render a per-vertex gradient on canvas.
 * Here the track is split into short chunks that each get a flat colour, which avoids the
 * forked dependency and its separate canvas pane.
 */

import { LatLng } from '../models/brouter-map.model';
import { distanceMetres } from './track-geometry';

/** ±8.5° is roughly ±15% incline, the range the legacy palette spanned. */
export const INCLINE_RANGE_DEGREES = 8.5;

/** Palette stops from the legacy `incline` provider. */
const PALETTE: { stop: number; rgb: [number, number, number] }[] = [
  { stop: 0, rgb: [0, 0, 255] },
  { stop: 0.25, rgb: [0, 255, 255] },
  { stop: 0.5, rgb: [0, 255, 0] },
  { stop: 0.75, rgb: [255, 255, 0] },
  { stop: 1, rgb: [255, 0, 0] }
];

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const toHex = (value: number): string => Math.round(value).toString(16).padStart(2, '0');

/** Linearly interpolates the palette. `t` is clamped to 0..1. */
export const inclineColour = (t: number): string => {
  const position = clamp01(t);

  for (let i = 1; i < PALETTE.length; i++) {
    const previous = PALETTE[i - 1];
    const current = PALETTE[i];
    if (position <= current.stop) {
      const span = current.stop - previous.stop;
      const ratio = span === 0 ? 0 : (position - previous.stop) / span;
      const rgb = previous.rgb.map((channel, c) => channel + (current.rgb[c] - channel) * ratio);
      return `#${rgb.map(toHex).join('')}`;
    }
  }

  return `#${PALETTE[PALETTE.length - 1].rgb.map(toHex).join('')}`;
};

/** Gradient between two track points, in degrees. Positive is uphill. */
export const inclineDegrees = (
  from: LatLng,
  to: LatLng,
  fromElevation: number | null,
  toElevation: number | null
): number => {
  if (fromElevation === null || toElevation === null) {
    return 0;
  }

  const distance = distanceMetres(from, to);
  if (distance === 0) {
    return 0;
  }

  return (Math.atan((toElevation - fromElevation) / distance) * 180) / Math.PI;
};

/** Maps a gradient in degrees onto the 0..1 palette position. */
export const inclineToPalettePosition = (degrees: number): number =>
  clamp01((degrees + INCLINE_RANGE_DEGREES) / (2 * INCLINE_RANGE_DEGREES));

export interface QualityChunk {
  points: LatLng[];
  colour: string;
}

/**
 * Splits the track into coloured chunks.
 *
 * Chunks overlap by one point so the rendered line has no visible gaps.
 */
export const buildInclineChunks = (
  track: readonly LatLng[],
  elevations: readonly (number | null)[],
  chunkSize = 8
): QualityChunk[] => {
  if (track.length < 2) {
    return [];
  }

  const chunks: QualityChunk[] = [];

  for (let start = 0; start < track.length - 1; start += chunkSize) {
    const end = Math.min(start + chunkSize, track.length - 1);
    const degrees = inclineDegrees(
      track[start],
      track[end],
      elevations[start] ?? null,
      elevations[end] ?? null
    );

    chunks.push({
      points: track.slice(start, end + 1),
      colour: inclineColour(inclineToPalettePosition(degrees))
    });
  }

  return chunks;
};
